import { api } from "./apiClient";

export interface PageAcl {
  [path: string]: string[];
}

export interface AclResponse {
  page_acl?: PageAcl;
}

let cached: PageAcl | null = null;
// In-Flight-Dedupe: Ohne dies feuert jeder gleichzeitige Aufrufer (Header prüft
// jeden Nav-Eintrag, dazu RequireAcl, personalNav, useSettingsAcl …) beim Laden
// einer Seite einen eigenen `GET /acl.json`. Das erzeugt ein Dutzend identischer
// Requests, überläuft das Browser-Verbindungslimit (~6/Host) und lässt Seiten
// „ewig" laden bzw. in den Timeout laufen. Wir cachen daher die *laufende*
// Promise, sodass parallele Aufrufer denselben Request teilen.
let inflight: Promise<PageAcl> | null = null;

export async function fetchPageAcl(): Promise<PageAcl> {
  if (cached) return cached;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const data = await api.get<AclResponse>("/acl.json");
      cached = (data?.page_acl || {}) as PageAcl;
      return cached;
    } catch {
      // Transienten Fehler NICHT dauerhaft cachen – sonst bliebe die Navigation
      // nach einem einzelnen Netzfehler bis zum Reload leer. Ein späterer Aufruf
      // darf erneut versuchen; gleichzeitige Aufrufer teilen sich diesen Versuch.
      return {} as PageAcl;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

export function buildGroupListFromAcl(acl: PageAcl): string[] {
  const set = new Set<string>();
  for (const groups of Object.values(acl)) {
    for (const g of groups || []) {
      if (g && g !== "*") set.add(g);
    }
  }
  return [...set].sort((a, b) => a.localeCompare(b, "de"));
}

export function listPagesFromAcl(acl: PageAcl): string[] {
  return Object.keys(acl)
    .filter((p) => p.endsWith(".html"))
    .sort((a, b) => a.localeCompare(b, "de"));
}

// CN=Foo,DC=… → Foo (gleiche Logik wie /static/acl.js)
export function normGroups(arr: string[] = []): string[] {
  return (arr || [])
    .map((s) => {
      if (typeof s !== "string") return s;
      const m = /^CN=([^,]+)/i.exec(s);
      return m && m[1] ? m[1] : s;
    })
    .filter((s): s is string => typeof s === "string");
}

// Pfadnormalisierung – muss exakt zur Server-ACL passen
export function normalizeAclPath(p: string = "/"): string {
  let x = (p || "/").trim().toLowerCase();
  if (!x.startsWith("/")) x = "/" + x;
  // /alt/-Präfix abstreifen, damit die ACL-Tabelle der klassischen Pfade greift
  if (x.startsWith("/alt/")) x = x.slice("/alt".length);
  if (x === "/alt") x = "/";
  if (x.endsWith("/")) return x + "index.html";
  const last = x.split("/").pop() || "";
  return last && last.includes(".") ? x : x + ".html";
}

export function isAllowed(userGroups: string[] = [], rule: string[] | undefined): boolean {
  if (!rule || rule.length === 0) return false;
  if (rule.includes("*")) return true;
  const g = normGroups(userGroups);
  return g.some((x) => rule.includes(x));
}

export async function canViewHref(href: string, userGroups: string[]): Promise<boolean> {
  const acl = await fetchPageAcl();
  const rule = acl[normalizeAclPath(href)];
  return isAllowed(userGroups, rule);
}
